<template>
  <Navbar />
  <!-- 主要內容區塊錨點 (搭配 Skip Link 與 Alt+C 快速鍵) -->
  <div id="main-content" tabindex="-1" class="main-content-anchor">
    <a
      id="accesskey-c"
      href="#accesskey-c"
      accesskey="C"
      class="accesskey-brick sr-only-focusable"
      title="主要內容區塊，快捷鍵：Alt+C"
      aria-label="主要內容區塊"
    >:::</a>
  </div>
  <div class="forgot-password-page">
    <div class="forgot-password-card">
      <div class="reset-pwd-title">
        <h2>{{ $t("smsAuth.resetTitle") }}</h2>
        <p>{{ $t("smsAuth.resetSubtitle") }}</p>
      </div>

      <!-- Step 1：輸入手機，送出驗證碼 -->
      <div v-if="step === 1" class="step-section">
        <a-form layout="vertical" :model="formModel" autocomplete="off" @finish="handleSendCode" @submit.prevent="handleSendCode">
          <a-form-item
            class="custom-form-item custom-input"
            :validate-status="fieldErrors.phone ? 'error' : ''"
            :help="fieldErrors.phone || ''"
          >
            <label class="form-label" for="resetPhoneInput">
              {{ $t("smsAuth.phoneLabel") }}<span class="required">*</span>
            </label>
            <a-input
              id="resetPhoneInput"
              v-model:value="formModel.phone"
              :placeholder="$t('smsAuth.phonePlaceholder')"
              :maxlength="15"
              :disabled="sending"
              autocomplete="off"
              name="resetPhone"
              :aria-label="$t('smsAuth.phoneLabel')"
              :title="$t('smsAuth.phoneLabel')"
              @input="() => clearFieldError('phone')"
            />
          </a-form-item>

          <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>

          <a-button
            class="submit-btn"
            type="primary"
            :loading="sending"
            :disabled="!isPhoneValid"
            size="large"
            block
            @click="handleSendCode"
          >
            <span v-if="!sending">{{ $t("smsAuth.sendCode") }}</span>
            <span v-else>Loading...</span>
          </a-button>
        </a-form>
      </div>

      <!-- Step 2：輸入驗證碼 + 新密碼，重設密碼 -->
      <div v-else class="step-section">
        <p class="phone-hint">
          {{ $t("smsAuth.registerSubtitle", { phone: maskPhone(formModel.phone) }) }}
        </p>

        <a-form layout="vertical" :model="formModel" autocomplete="off" @finish="handleReset" @submit.prevent="handleReset">
          <a-form-item
            class="custom-form-item custom-input"
            :validate-status="fieldErrors.code ? 'error' : ''"
            :help="fieldErrors.code || ''"
          >
            <label class="form-label" for="resetCodeInput">
              {{ $t("smsAuth.codeLabel") }}<span class="required">*</span>
            </label>
            <a-input
              id="resetCodeInput"
              v-model:value="formModel.code"
              :placeholder="$t('smsAuth.codePlaceholder')"
              :maxlength="6"
              autocomplete="one-time-code"
              name="resetCode"
              :aria-label="$t('smsAuth.codeLabel')"
              :title="$t('smsAuth.codeLabel')"
              @input="onCodeInput"
            />
          </a-form-item>

          <a-form-item
            class="custom-form-item custom-input"
            :validate-status="fieldErrors.password ? 'error' : ''"
            :help="fieldErrors.password || ''"
            style="margin-top: 16px"
          >
            <label class="form-label" for="resetPasswordInput">
              {{ $t("smsAuth.newPasswordLabel") }}<span class="required">*</span>
            </label>
            <a-input-password
              id="resetPasswordInput"
              v-model:value="formModel.password"
              :placeholder="$t('smsAuth.newPasswordPlaceholder')"
              autocomplete="new-password"
              name="newPassword"
              :aria-label="$t('smsAuth.newPasswordLabel')"
              :title="$t('smsAuth.newPasswordLabel')"
              @input="() => clearFieldError('password')"
            />
          </a-form-item>

          <a-form-item
            class="custom-form-item custom-input"
            :validate-status="fieldErrors.passwordConfirmation ? 'error' : ''"
            :help="fieldErrors.passwordConfirmation || ''"
            style="margin-top: 16px"
          >
            <label class="form-label">
              {{ $t("smsAuth.confirmPasswordLabel") }}<span class="required">*</span>
            </label>
            <a-input-password
              v-model:value="formModel.passwordConfirmation"
              :placeholder="$t('smsAuth.confirmPasswordPlaceholder')"
              autocomplete="new-password"
              name="confirmPassword"
            />
          </a-form-item>

          <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>

          <a-button
            class="submit-btn"
            type="primary"
            :loading="submitting"
            size="large"
            block
            @click="handleReset"
          >
            <span v-if="!submitting">{{ $t("smsAuth.resetSubmit") }}</span>
            <span v-else>Loading...</span>
          </a-button>

          <div class="resend-section">
            <a-button type="link" :disabled="resendCountdown > 0 || resendLoading" @click="handleSendCode">
              <span v-if="resendLoading">Loading...</span>
              <span v-else-if="resendCountdown > 0">
                {{ $t("smsAuth.resendIn", { seconds: resendCountdown }) }}
              </span>
              <span v-else>{{ $t("smsAuth.resendCode") }}</span>
            </a-button>
          </div>
        </a-form>
      </div>

      <div class="back-to-login">
        <router-link to="/sign-in" class="toc-link">
          <span>{{ $t("resendEmail.backToLogin") || "返回登入" }}</span>
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import Navbar from "../components/Navbar.vue";
import { reactive, ref, computed, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { message } from "ant-design-vue";
import { authAPI } from "@/services/api.js";

const router = useRouter();
const { t: $t } = useI18n();

const step = ref(1);
const sending = ref(false);
const submitting = ref(false);
const resendLoading = ref(false);
const resendCountdown = ref(0);
const errorMessage = ref("");

let countdownTimer = null;

const formModel = reactive({
  phone: "",
  code: "",
  password: "",
  passwordConfirmation: "",
});

const fieldErrors = reactive({});

const isEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
const isPhone = (val) => /^[0-9]{8,15}$/.test(val.replace(/[\s\-]/g, ""));

const isPhoneValid = computed(() => {
  const val = formModel.phone.trim();
  return isEmail(val) || isPhone(val);
});

const clearFieldError = (key) => {
  if (fieldErrors[key]) delete fieldErrors[key];
  if (errorMessage.value) errorMessage.value = "";
};

const onCodeInput = () => {
  formModel.code = formModel.code.replace(/\D/g, "").slice(0, 6);
  if (fieldErrors.code) delete fieldErrors.code;
  if (errorMessage.value) errorMessage.value = "";
};

const maskPhone = (p) => {
  if (!p || p.length < 4) return p;
  return p.slice(0, 3) + "***" + p.slice(-3);
};

const startCountdown = () => {
  resendCountdown.value = 60;
  if (countdownTimer) clearInterval(countdownTimer);
  countdownTimer = setInterval(() => {
    resendCountdown.value--;
    if (resendCountdown.value <= 0) {
      clearInterval(countdownTimer);
      countdownTimer = null;
    }
  }, 1000);
};

const handleSendCode = async () => {
  const account = formModel.phone.trim();
  if (!isPhoneValid.value) {
    fieldErrors.phone = "請輸入有效的手機號碼或電子郵件";
    return;
  }

  if (step.value === 1) sending.value = true;
  else resendLoading.value = true;

  try {
    if (isEmail(account)) {
      await authAPI.forgotPassword({ email: account });
      sessionStorage.setItem("reset_email", account);
      message.success($t("resendEmail.resetLinkSent") || "重設密碼連結已發送到您的信箱");
      router.push("/check-email");
    } else {
      const normalizedPhone = account.replace(/[\s\-]/g, "");
      formModel.phone = normalizedPhone;
      await authAPI.sendResetSms({ phone: normalizedPhone });
      if (step.value === 1) {
        step.value = 2;
        message.success($t("smsAuth.resendSuccess") || "驗證碼已寄送");
      } else {
        message.success($t("smsAuth.resendSuccess") || "驗證碼已重新發送");
      }
      startCountdown();
    }
  } catch (error) {
    const status = error?.response?.status;
    if (status === 404) {
      errorMessage.value = $t("smsAuth.errorPhoneNotBound") || "此帳號或手機未綁定";
    } else if (status === 400) {
      errorMessage.value = error?.response?.data?.message || $t("smsAuth.errorRateLimit");
    } else {
      errorMessage.value = error?.response?.data?.message || "寄送失敗";
    }
  } finally {
    sending.value = false;
    resendLoading.value = false;
  }
};

const validateResetForm = () => {
  Object.keys(fieldErrors).forEach((k) => delete fieldErrors[k]);
  let isValid = true;

  if (!/^[0-9]{6}$/.test(formModel.code)) {
    fieldErrors.code = $t("smsAuth.errorCodeInvalid");
    isValid = false;
  }
  if (!formModel.password || formModel.password.length < 8) {
    fieldErrors.password = "密碼至少 8 個字元";
    isValid = false;
  } else {
    const hasUpper = /[A-Z]/.test(formModel.password);
    const hasLower = /[a-z]/.test(formModel.password);
    const hasNum = /[0-9]/.test(formModel.password);
    const hasSpec = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formModel.password);
    if (!(hasUpper && hasLower && hasNum && hasSpec)) {
      fieldErrors.password = "密碼須含大小寫英文、數字與特殊字元";
      isValid = false;
    }
  }
  if (formModel.password !== formModel.passwordConfirmation) {
    fieldErrors.passwordConfirmation = "兩次密碼輸入不一致";
    isValid = false;
  }
  return isValid;
};

const handleReset = async () => {
  if (!validateResetForm()) return;
  submitting.value = true;
  try {
    const response = await authAPI.resetPasswordBySms({
      phone: formModel.phone.trim(),
      code: formModel.code,
      password: formModel.password,
      password_confirmation: formModel.passwordConfirmation,
    });
    if (response.status >= 200 && response.status < 300) {
      message.success($t("smsAuth.successReset"));
      router.push("/sign-in");
    } else {
      errorMessage.value = response.data?.message || $t("smsAuth.errorCodeInvalid");
    }
  } catch (error) {
    errorMessage.value = error?.response?.data?.message || $t("smsAuth.errorCodeInvalid");
  } finally {
    submitting.value = false;
  }
};

onUnmounted(() => {
  if (countdownTimer) clearInterval(countdownTimer);
});
</script>

<style scoped>
.forgot-password-page {
  width: 100%;
  min-height: 100vh;
  background-color: var(--color-primary-bg);
  background: linear-gradient(180deg, #f9f9f9, #a7dff1);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 150px 16px 24px;
  box-sizing: border-box;
}

.forgot-password-card {
  width: 100%;
  max-width: 550px;
  background: #ffffff;
  border-radius: 20px;
  padding: 40px 32px;
  box-sizing: border-box;
}

.reset-pwd-title {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 32px;
  text-align: center;
}

.reset-pwd-title h2 {
  font-size: 30px;
  font-weight: 700;
  color: #000;
  margin-bottom: 12px;
}

.reset-pwd-title p {
  color: #797979;
  font-size: 16px;
  margin: 0;
  text-align: center;
}

.step-section {
  width: 100%;
}

.custom-form-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.form-label {
  font-size: 16px;
  font-weight: 500;
  color: #696969;
}

.required {
  color: #ff4d4f;
  margin-left: 2px;
}

.custom-input :deep(.ant-input),
.custom-input :deep(.ant-input-affix-wrapper),
.custom-input :deep(.ant-input-password) {
  height: 64px !important;
  border-radius: 20px !important;
  padding: 0 16px !important;
  font-size: 16px !important;
}

.custom-input :deep(.ant-input-password .ant-input) {
  border: none !important;
  box-shadow: none !important;
  height: 100% !important;
  padding: 0 !important;
}

.custom-input :deep(.ant-input:focus),
.custom-input :deep(.ant-input-affix-wrapper:focus) {
  border-color: #707070 !important;
  box-shadow: none !important;
}

.phone-hint {
  text-align: center;
  color: #888;
  margin-bottom: 16px;
  font-size: 14px;
}

.error-message {
  color: #ff4d4f;
  font-size: 14px;
  margin: 8px 0 16px;
  text-align: center;
}

.submit-btn {
  height: 64px;
  border-radius: 999px;
  background-color: var(--color-primary);
  color: #ffffff;
  font-size: 18px;
  font-weight: 500;
  margin-top: 16px;
}

.submit-btn:hover {
  background-color: var(--color-primary-hover);
  color: #ffffff;
}

.submit-btn:disabled {
  background-color: #d9d9d9 !important;
  color: #00000040 !important;
  border-color: #d9d9d9 !important;
  cursor: not-allowed;
}

.resend-section {
  text-align: center;
  margin-top: 16px;
}

.back-to-login {
  text-align: center;
  margin-top: 24px;
}

.toc-link {
  color: var(--color-primary);
  text-decoration: underline;
  font-weight: 500;
}

@media (max-width: 768px) {
  .forgot-password-page {
    padding: 80px 16px 24px;
  }
  .forgot-password-card {
    padding: 32px 20px;
  }
  .reset-pwd-title h2 {
    font-size: 24px;
  }
  .submit-btn {
    height: 56px;
    font-size: 16px;
  }
}
</style>
